import { useMutation } from '@apollo/client'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { CleanOrderStatus } from '@src/../__generated__/globalTypes'
import { Button, ButtonType } from '@src/components/Button'
import { breakpoints } from '@src/constants/breakpoints'
import { apolloErrorParser } from '@src/utils/apolloErrorParser/apolloErrorParser'

import {
  ReviewContainer,
  ReviewTitle,
  ReviewText,
  ReviewErrorText,
  ReviewOrderNumberRow,
} from './OrderReview.styles'
import {
  AddReviewDocument,
  AddReviewMutationVariables,
} from './mutations/__generated__/addReview.graphql-interface'
import { SuccessScreen } from '../../components/BurgerMenu/SuccessScreen'
import { MainRouteName } from '../../hooks/useAccountRouter'
import {
  MerchantType,
  NarrowFulfilmentMethodInputType,
} from '../../graphql-types'
import { useMarketplace } from '@src/hooks/useMarketplace'

const ratingOptions = [
  { value: '', label: '—' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
]

const ReviewForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`

const RatingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: ${breakpoints.largeTablet}px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
`

const Select = styled.select`
  height: 56px;
  border-radius: 8px;
  border: 2px solid #ebecf0;
  padding: 0 14px;
  background: #fff;
  font-size: 16px;
  font-family: inherit;
  color: inherit;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23a0a4b6' d='M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;

  &:focus {
    outline: none;
    border-color: #000;
  }
`

const TextArea = styled.textarea`
  min-height: 120px;
  border-radius: 8px;
  border: 2px solid #ebecf0;
  padding: 12px 14px;
  background: #fff;
  font-size: 16px;
  font-family: inherit;
  color: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #000;
  }
`

export const OrderReviewForm: React.FC<{
  cleanOrderStatus: CleanOrderStatus
  fulfilmentMethod: NarrowFulfilmentMethodInputType
  orderId: string
  orderNumber: string
  outletName: string
  allowReviewText: boolean
}> = ({
  cleanOrderStatus,
  fulfilmentMethod,
  orderId,
  orderNumber,
  outletName,
  allowReviewText,
}) => {
  const { t } = useTranslation('singleOrder')
  const [foodQuality, setFoodQuality] = useState('')
  const [restaurantService, setRestaurantService] = useState('')
  const [deliveryTime, setDeliveryTime] = useState('')
  const [reviewText, setReviewText] = useState('')
  const [submitted, setSubmitted] = useState<{
    status: string | null
  } | null>(null)

  const [error, setError] = useState<string | null>(null)

  const canReview = cleanOrderStatus === CleanOrderStatus.COMPLETE
  const marketplace = useMarketplace()

  const [submitReview, { loading }] = useMutation(AddReviewDocument, {
    onCompleted: data => {
      setError(null)
      setSubmitted({ status: data.addReview.review.status })
    },
    onError: err => {
      const parsed = apolloErrorParser(err)
      setError(
        parsed.genericClientErrors?.[0]?.message ||
          parsed.customClientErrors?.[0]?.message ||
          err.message
      )
    },
  })

  const hasInput = useMemo(() => {
    if (fulfilmentMethod === NarrowFulfilmentMethodInputType.DELIVERY) {
      return (
        foodQuality !== '' && restaurantService !== '' && deliveryTime !== ''
      )
    } else return foodQuality !== '' && restaurantService !== ''
  }, [foodQuality, restaurantService, deliveryTime])

  if (!canReview) {
    return (
      <ReviewContainer noBorder>
        <ReviewTitle>{t('leave_review', { outletName })}</ReviewTitle>
        <ReviewText>{t('review_unavailable_status')}</ReviewText>
      </ReviewContainer>
    )
  }

  return (
    <ReviewContainer noBorder>
      <ReviewTitle>{t('leave_review', { outletName })}</ReviewTitle>
      {submitted ? (
        <SuccessScreen
          successText={t('review_thank_you_pending')}
          returnToRoute={{
            mainRouteName: MainRouteName.ORDERS,
            pageDataId: orderId,
          }}
        />
      ) : (
        <ReviewForm
          onSubmit={async e => {
            e.preventDefault()

            if (!hasInput) {
              setError(t('review_validation_required'))
              return
            }

            setError(null)

            const variables: AddReviewMutationVariables = {
              orderId,
              foodQuality: Number(foodQuality),
              restaurantService: Number(restaurantService),

              deliveryTime: deliveryTime ? Number(deliveryTime) : undefined,
              reviewText: reviewText.trim() || undefined,
            }

            await submitReview({ variables })
          }}
        >
          <RatingGrid>
            <Field>
              <span>
                {t(
                  marketplace.merchantType === MerchantType.RETAIL
                    ? 'review_quality_retail'
                    : 'review_quality_takeaway'
                )}
              </span>
              <Select
                value={foodQuality}
                onChange={e => setFoodQuality(e.target.value)}
              >
                {ratingOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field>
              <span>{t('review_restaurant_service')}</span>
              <Select
                value={restaurantService}
                onChange={e => setRestaurantService(e.target.value)}
              >
                {ratingOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            {fulfilmentMethod === NarrowFulfilmentMethodInputType.DELIVERY && (
              <Field>
                <span>{t('review_delivery_time')}</span>
                <Select
                  value={deliveryTime}
                  onChange={e => setDeliveryTime(e.target.value)}
                >
                  {ratingOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </RatingGrid>

          {allowReviewText && (
            <Field>
              <span>{t('review_comment_optional')}</span>
              <TextArea
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
                placeholder={t('review_comment_placeholder')}
                maxLength={2000}
              />
            </Field>
          )}

          {error && <ReviewErrorText>{error}</ReviewErrorText>}

          <Button
            buttonType={ButtonType.PRIMARY}
            type="submit"
            content={t('review_submit')}
            loading={loading}
            disabled={!hasInput}
          />
        </ReviewForm>
      )}
      <ReviewOrderNumberRow>
        <span>#{orderNumber}</span>
      </ReviewOrderNumberRow>
    </ReviewContainer>
  )
}
